import { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowDown, ArrowRight, Check, Clock, MapPin, Users } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useAuthModal } from '../contexts/AuthModalContext';
import { Button, OroLoader } from '../components';
import PublicHeader from '../components/PublicHeader';
import StaticFooter from '../components/StaticFooter';
import AppStoreBadge from '../components/AppStoreBadge';
import OroTrademark from '../components/OroTrademark';
import EnvironmentBanner from '../components/EnvironmentBanner';
import Reveal from '../components/marketing/Reveal';
import SectionHead from '../components/marketing/SectionHead';
import { LEADERS } from '../data/content/leaders';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { MARKETING_PAGE_META } from '../lib/seo/pageMeta';

/**
 * "For club leaders" — the argument page (#3125, child of #3122).
 *
 * The WHY, not the how-to: `/getting-started` (LeaderGettingStartedPage) teaches
 * a leader who already decided; this page argues why a volunteer organiser
 * should run their club here at all. The two link to each other (the close CTA
 * points at the guide) and do not overlap.
 *
 * Designed through the assembly / oro-design-discovery process on #3125:
 *  - A0 sentence + A1 job list recorded on the issue; Gate 1/2/3 passed there.
 *  - Consumes the #3122 "Ten minutes before" anchor (no second identity).
 *  - COPY lives in `data/content/leaders.ts` (check-voice scans it; the gallery
 *    round-trip edits it). Do not inline strings here (#2954).
 *  - ™ CADENCE (Core Rule 24): exactly ONE mark, in the hero subhead via
 *    OroTrademark. Gold is rationed to that mark and the single close action
 *    (#3122 anchor); nothing else on the page is gold.
 *  - Authed visitors redirect to /home, same as the front door — this is a
 *    prospect surface, and it keeps PublicHeader + CTAs logged-out-only.
 *
 * Section order IS the A1 job list; changing it without re-running that pass
 * breaks the argument. Shared Reveal/SectionHead extracted from HomePage here,
 * its second consumer (toward the #3216 public-page kit).
 */

const HERO_IMAGE = '/images/renato-leal-t7nrCrckBmc-unsplash.jpg';

/**
 * Every section reveals only once it is ~30% up from the bottom of the viewport,
 * not the shared default (-8%). The default fires as soon as a section's top
 * PEEKS into view, so a lower section animates while the visitor is still on the
 * one above it. This later trigger makes the sections reveal one at a time as
 * you scroll to each — cross-section sequencing is scroll, not the per-element
 * `delay` (which only orders elements WITHIN a section, once it has triggered).
 */
const REVEAL_MARGIN = '0px 0px -30% 0px';

/** S1 — hero. Tell the volunteer this is for them and the load lightens. */
function Hero() {
  return (
    <section className="relative flex items-end overflow-hidden min-h-[min(88vh,48rem)] pb-18">
      <div
        className="leaders-hero-scrim absolute inset-0 z-0 bg-cover"
        style={{ backgroundImage: `url('${HERO_IMAGE}')`, backgroundPosition: '50% 32%' }}
        aria-hidden="true"
      />
      <div className="relative z-10 w-full max-w-5xl mx-auto px-6">
        <div className="max-w-xl">
          <h1 className="text-hero font-bold leading-[1.04] tracking-tight text-text-primary">
            {LEADERS.hero.headline}
          </h1>
          <p className="mt-5 text-base text-text-secondary max-w-lg">
            <OroTrademark /> {LEADERS.hero.subAfterBrand}
          </p>
          <div className="mt-9">
            <a
              href="#see"
              className="inline-flex items-center gap-2 rounded-lg border border-oro-border-hover px-6 py-3 text-sm font-semibold text-text-primary hover:border-oro-gold hover:text-oro-gold oro-transition"
            >
              {LEADERS.hero.primaryCta}
              <ArrowDown className="w-4 h-4" aria-hidden="true" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/** S2 — the reality. Recognition before solution; no product yet.
 *  The four lines ACCRUE with a slow stagger (O1) and the punchline lands after
 *  a beat (O3). The turn splits: "everyone sees the event" arrives from the
 *  left, "you see everything" from the right — the two viewpoints converging on
 *  the same event from opposite sides. `rootMargin` holds every reveal until the
 *  section is well in view, so it does not fire on load before the visitor
 *  scrolls to it. */
function RealitySection() {
  const c = LEADERS.reality;
  // Slow accrual, then a longer beat before the fourth line (the punchline).
  const lineDelays = [0, 280, 560, 950];
  const MARGIN = REVEAL_MARGIN;
  return (
    <section className="overflow-x-clip py-19">
      <div className="max-w-5xl mx-auto px-6">
        <Reveal rootMargin={MARGIN}>
          <span className="block text-xs font-semibold uppercase tracking-widest text-oro-club">
            {c.eyebrow}
          </span>
        </Reveal>
        <div className="mt-6 max-w-2xl">
          {c.lines.map((line, i) => (
            <Reveal key={i} delay={lineDelays[i] ?? i * 280} rootMargin={MARGIN}>
              <p className="mb-4 text-2xl font-semibold leading-tight tracking-tight text-text-primary sm:text-3xl">
                {line.text}
              </p>
            </Reveal>
          ))}
        </div>
        {/* Delays sequence the turn AFTER the last line (punchline at 950): the
            two halves converge from opposite sides once the accrual has landed. */}
        <div className="mt-8 max-w-xl text-lg">
          <Reveal from="left" delay={1200} rootMargin={MARGIN}>
            <p className="text-text-secondary">{c.turnLead}</p>
          </Reveal>
          <Reveal from="right" delay={1450} rootMargin={MARGIN}>
            <p className="mt-1 font-semibold text-text-primary">{c.turnRest}</p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/** S3 — three burdens lifted. Type only, no icons (anchor rations icons; the
 *  front door's three-item section set the precedent). */
function BurdensSection() {
  const c = LEADERS.burdens;
  return (
    <>
      <hr className="border-oro-border" />
      <section className="overflow-x-clip py-19">
        <div className="max-w-5xl mx-auto px-6">
          <Reveal rootMargin={REVEAL_MARGIN}>
            <SectionHead eyebrow={c.eyebrow} title={c.title} />
          </Reveal>
          <div className="mt-11 grid gap-10 md:grid-cols-3 md:gap-12">
            {c.items.map((item, i) => (
              <Reveal key={item.heading} from="left" delay={i * 90} rootMargin={REVEAL_MARGIN}>
                <div className="mb-4 h-0.5 w-7 rounded bg-oro-club/70" aria-hidden="true" />
                <h3 className="mb-2.5 text-lg font-semibold text-text-primary">{item.heading}</h3>
                <p className="text-text-secondary">{item.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

/** S4 — see it work. Seeded Golden Hour demo, from the leader's side. Real
 *  product surfaces, never a real club (#3122 imagery rule). */
function SeeItSection() {
  const c = LEADERS.seeit;
  return (
    <>
      <hr className="border-oro-border" />
      <section id="see" className="overflow-x-clip py-19">
        <div className="max-w-5xl mx-auto px-6">
          <Reveal rootMargin={REVEAL_MARGIN}>
            <SectionHead eyebrow={c.eyebrow} title={c.title} lede={c.lede} />
          </Reveal>

          <div className="mt-11 grid gap-4 md:grid-cols-[1.1fr_0.9fr] md:items-start">
            {/* Event */}
            <Reveal from="left" delay={90} rootMargin={REVEAL_MARGIN}>
              <div className="rounded-xl border border-oro-border bg-oro-surface p-5 shadow-card">
                <span className="block font-mono text-xs uppercase tracking-widest text-text-tertiary">
                  {c.eventCap}
                </span>
                <div className="mt-4 flex items-start gap-3">
                  <div className="flex-none rounded-lg border border-oro-border bg-oro-surface-alt px-3 py-2 text-center">
                    <span className="block text-xl font-bold leading-none text-text-primary">
                      {c.eventDay}
                    </span>
                    <span className="mt-1 block text-xs uppercase tracking-wider text-text-muted">
                      {c.eventMonth}
                    </span>
                  </div>
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-base font-semibold text-text-primary">{c.eventTitle}</h3>
                      <span className="rounded-full border border-oro-club/30 px-2 py-0.5 text-[0.65rem] font-medium text-oro-club">
                        {c.eventRecurrence}
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-text-muted">
                      <span className="inline-flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5" aria-hidden="true" />
                        {c.eventMeta}
                      </span>
                    </div>
                    <p className="mt-3 text-xs text-text-secondary">{c.eventBlurb}</p>
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-2 border-t border-oro-border pt-4">
                  <Users className="h-4 w-4 text-oro-club" aria-hidden="true" />
                  <span className="text-sm text-text-secondary">{c.eventGoing}</span>
                </div>
              </div>
            </Reveal>

            <Reveal className="flex flex-col gap-4" from="right" delay={170} rootMargin={REVEAL_MARGIN}>
              {/* Check-in */}
              <div className="rounded-xl border border-oro-border bg-oro-surface p-5 shadow-card">
                <span className="block font-mono text-xs uppercase tracking-widest text-text-tertiary">
                  {c.checkinCap}
                </span>
                <div className="mt-4 flex items-center gap-3">
                  <span className="grid h-8 w-8 flex-none place-items-center rounded-full bg-oro-club/15 text-oro-club">
                    <Check className="w-4 h-4" aria-hidden="true" />
                  </span>
                  <div>
                    <span className="block text-2xl font-bold leading-none text-text-primary tabular-nums">
                      {c.checkinCount}
                    </span>
                    <span className="block text-xs text-text-muted">{c.checkinLabel}</span>
                  </div>
                </div>
                <p className="mt-4 text-xs text-text-secondary">{c.checkinBlurb}</p>
              </div>

              {/* Announcement */}
              <div className="rounded-xl border border-oro-border bg-oro-surface p-5 shadow-card">
                <span className="block font-mono text-xs uppercase tracking-widest text-text-tertiary">
                  {c.annCap}
                </span>
                <div className="mt-3 rounded-lg bg-oro-surface-alt p-3.5">
                  <span className="mb-1.5 flex items-center gap-1.5 text-xs text-text-muted">
                    <MapPin className="w-3 h-3" aria-hidden="true" />
                    {c.annPin}
                  </span>
                  <p className="text-sm text-text-primary">{c.annMsg}</p>
                  <p className="mt-2 text-xs text-text-tertiary">{c.annTo}</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}

/** S6 — a week in the life. Ends on the payoff: you got to go too. */
function WeekSection() {
  const c = LEADERS.week;
  return (
    <>
      <hr className="border-oro-border" />
      <section className="overflow-x-clip py-19">
        <div className="max-w-5xl mx-auto px-6">
          <Reveal rootMargin={REVEAL_MARGIN}>
            <SectionHead eyebrow={c.eyebrow} title={c.title} />
          </Reveal>
          <div className="mt-10 max-w-3xl">
            {c.steps.map((step, i) => (
              <Reveal key={step.when} from="left" delay={i * 70} rootMargin={REVEAL_MARGIN}>
                <div
                  className={`grid grid-cols-1 gap-1 py-4 sm:grid-cols-[9rem_1fr] sm:gap-6${
                    i === 0 ? '' : ' border-t border-oro-border'
                  }`}
                >
                  <span className="text-sm font-semibold text-oro-club">{step.when}</span>
                  <p className="text-base text-text-primary">
                    {step.what}
                    {step.sub && <span className="text-text-muted"> {step.sub}</span>}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

/** S8 — close. The page's one gold action + the link to the how-to. */
function CloseSection() {
  const { openLoginModal } = useAuthModal();
  const c = LEADERS.close;
  return (
    <>
      <hr className="border-oro-border" />
      <section className="py-24 text-center">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-section font-bold tracking-tight text-text-primary">{c.title}</h2>
          <p className="mx-auto mt-4 max-w-md text-text-secondary">{c.body}</p>
          <Button
            variant="primary"
            size="lg"
            className="mt-8 py-3 px-8"
            onClick={() => openLoginModal()}
          >
            {c.cta}
          </Button>
          {/* #3448: the App Store note + badge under the close CTA. The note is
              copy (voice-gated in leaders.ts); the badge is Apple's image, so it
              adds no ™ to this page's single mark (the hero subhead). */}
          <div className="mt-6 flex flex-col items-center gap-3">
            <p className="text-sm text-text-muted">{c.appStoreNote}</p>
            <AppStoreBadge />
          </div>
          <p className="mt-6 text-sm text-text-muted">
            {c.guideLead}{' '}
            <Link
              to="/getting-started"
              className="inline-flex items-center gap-1 font-semibold text-oro-club hover:text-oro-gold oro-transition"
            >
              {c.guideLink}
              <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
            </Link>
          </p>
        </div>
      </section>
    </>
  );
}

function LeaderPage() {
  return (
    <div className="min-h-screen bg-oro-bg text-text-primary flex flex-col">
      <EnvironmentBanner />
      <PublicHeader />
      <main className="flex-1">
        <Hero />
        <RealitySection />
        <BurdensSection />
        <SeeItSection />
        <WeekSection />
        <CloseSection />
      </main>
      <StaticFooter />
    </div>
  );
}

export default function LeaderWhyPage() {
  useDocumentTitle(MARKETING_PAGE_META['/leaders'].title, { suffix: false });
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      // `replace` so Back does not return to /leaders and loop the redirect.
      navigate('/home', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Gate on isAuthenticated too, not just authLoading: without it an
  // authenticated visitor paints the full logged-out page for one frame before
  // the effect redirects (George M1 on #3218, same shape here). Render the
  // loader until the redirect lands.
  if (authLoading || isAuthenticated) {
    return (
      <div className="min-h-screen bg-oro-bg flex items-center justify-center">
        <OroLoader label="Loading" size={72} />
      </div>
    );
  }

  return <LeaderPage />;
}
